import clsx from 'clsx';
import {
  type PropsWithChildren,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  type MouseEvent as ReactMouseEvent,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';
import {
  ToastContainer as ReactToastifyContainer,
  cssTransition,
  toast,
} from 'react-toastify';
import { useSyncExternalStoreWithSelector } from 'use-sync-external-store/shim/with-selector';

import { XmarkIcon } from '@dynatrace/strato-icons';

import * as styles from './ToastContainer.sty.js';
import { Button } from '../../buttons/button/Button.js';
import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { useSafeForwardProps } from '../../core/hooks/useSafeForwardProps.js';
import { createStore } from '../../core/store/store.js';
import { AriaLabelingProps } from '../../core/types/a11y-props.js';
import { DataTestId } from '../../core/types/data-props.js';
import { MaskingProps } from '../../core/types/masking-props.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { Flex } from '../../layouts/flex/Flex.js';
import { useKeyboardFocusHandling } from '../../overlays/hooks/useKeyboardFocusHandling.js';

const COMPONENT_NAME = 'ToastContainer';

type ToastContainerProps = PropsWithChildren &
  StylingProps &
  DataTestId &
  MaskingProps &
  AriaLabelingProps;

let unique = 0;

const toastContainerStore = createStore<{ currentContainerId: number | null }>({
  currentContainerId: null,
});

/**
 * Use Toast to show users live, time-sensitive messages about system status changes. Toast variants for four different kinds of messages are provided.
 * @public
 */
export const ToastContainer = forwardRef<HTMLDivElement, ToastContainerProps>(
  (props, forwardedRef) => {
    const {
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      ...remainingProps
    } = props;
    const closeButtonRef = useRef<HTMLButtonElement>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);

    const ariaLabelingProps = useAriaLabelingProps(remainingProps);
    const forwardProps = useSafeForwardProps(remainingProps, ariaLabelingProps);
    const { 'aria-label': ariaLabel, ...remainingAriaLabelingProps } =
      ariaLabelingProps;

    const [handleFocusOnKeydown, closedOnKeydown] = useKeyboardFocusHandling(
      closeButtonRef,
      containerRef,
    );

    const rootRef = useMergeRefs([forwardedRef, containerRef]);

    const CloseButton = ({
      closeToast,
    }: {
      closeToast: (event: ReactMouseEvent<HTMLElement>) => void;
    }) => {
      const intl = useIntl();

      const closeButtonLabel = intl.formatMessage({
        defaultMessage: 'Close notification',
        id: '7IzOa/lx4Dc/qLGo',
        description: 'Aria-label for the close notification button.',
      });

      // Handle the escape key
      const closeToastOnEsc = (event: ReactKeyboardEvent) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          closedOnKeydown.current = true;
          // Limited typing on the end of the react-toastify library
          // requires a `React.MouseEvent` here, but they never actually
          // use anything very specific to the MouseEvent.
          closeToast(event as unknown as ReactMouseEvent<HTMLElement>);
        }
      };

      /** Only when the toast is closed by the keyboard we need to handle the focus */
      const detectCloseOnKeyDown = useCallback(() => {
        closedOnKeydown.current = true;
      }, []);

      return (
        <Flex
          flexDirection="column"
          alignItems="flex-start"
          paddingTop={6}
          paddingRight={6}
          onKeyDown={closeToastOnEsc}
        >
          <Button
            onKeyDown={detectCloseOnKeyDown}
            onClick={closeToast}
            aria-label={closeButtonLabel}
            ref={closeButtonRef}
            variant="default"
            onPointerDown={(e: ReactMouseEvent) => e.stopPropagation()}
            data-dt-name="CloseX"
          >
            <Button.Prefix>
              <XmarkIcon />
            </Button.Prefix>
          </Button>
        </Flex>
      );
    };

    const containerId = useRef(++unique);

    const activeId = useSyncExternalStoreWithSelector(
      toastContainerStore.subscribe,
      () => toastContainerStore.getState(),
      null,
      (state) => state.currentContainerId,
    );

    useEffect(() => {
      const id = containerId.current;
      if (toastContainerStore.getState().currentContainerId === null) {
        toastContainerStore.setState({ currentContainerId: id });
      }
    }, [activeId]);

    useEffect(() => {
      const id = containerId.current;
      let pendingObserver: MutationObserver | null = null;

      const unsubscribeOnChange = toast.onChange((payload) => {
        if (payload.status === 'removed') {
          const container = containerRef.current;
          if (!container) {
            return;
          }

          // in react-toastify v11.1.0+, toast.onChange with status: 'removed' fires synchronously before the exit animation,
          // as opposed to firing after the exit animation as it was before. Therefore, a mutation observer is needed
          // to wait for the toast element to actually be removed from DOM before handling focus.
          pendingObserver?.disconnect();

          pendingObserver = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
              if (mutation.removedNodes.length > 0) {
                pendingObserver?.disconnect();
                pendingObserver = null;
                handleFocusOnKeydown();
                return;
              }
            }
          });

          pendingObserver.observe(container, {
            childList: true,
            subtree: true,
          });
        }
      });

      return function cleanup() {
        unsubscribeOnChange();
        pendingObserver?.disconnect();

        if (toastContainerStore.getState().currentContainerId === id) {
          toastContainerStore.setState({ currentContainerId: null });
        }
      };
      // excluded handleFocusOnKeydown dependency, because this effect needs to be executed once for toast.onChange
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (activeId !== containerId.current) {
      return null;
    }

    return createPortal(
      <div
        ref={rootRef}
        className={clsx(consumerClassName, styles.root)}
        style={consumerStyle}
        // This data attribute is necessary to ensure the correct stacking order with the Microguide and FeatureHighlight.
        data-toast-container
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        {...remainingAriaLabelingProps}
        {...forwardProps}
        data-dt-component={COMPONENT_NAME}
      >
        <ReactToastifyContainer
          hideProgressBar={true}
          draggable={false}
          closeButton={CloseButton}
          limit={5}
          pauseOnHover={true}
          closeOnClick={false}
          newestOnTop={false}
          // Have the default if not provided ("Notifications Alt+T") or custom.
          {...(ariaLabel && { 'aria-label': ariaLabel })}
          transition={cssTransition({
            collapseDuration: 200,
            enter: 'strato__toast__enter',
            exit: 'strato__toast__exit',
          })}
        />
      </div>,
      document.body,
    );
  },
);

(
  ToastContainer as typeof ToastContainer & { displayName: string }
).displayName = COMPONENT_NAME;
